import { ContentEntitySys, FieldAppSDK } from '@contentful/app-sdk'
import { useSDK } from '@contentful/react-apps-toolkit'
import { Preloader } from '@vp/contentful-app/components'
import React, { useEffect, useMemo } from 'react'
import { DefaultEntryField } from './entry-fields/DefaultEntryField'

const ENTRY_FIELDS = {
  default: DefaultEntryField,
  // TODO: Add your entry field editors here
}

export interface FieldProps {
  data: any
}

export type FieldComponent = React.FC<FieldProps>

const Field = () => {
  const sdk = useSDK<FieldAppSDK>()

  const [fieldValue, setFieldValue] = React.useState<any | undefined | null>(undefined)
  const [version, setVersion] = React.useState<number | undefined>(undefined)

  const onSysChanged = (sys: ContentEntitySys) => {
    if (sys.version !== version) {
      setVersion(sys.version)
    }
  }
  const onSysChangeRef = React.useRef<(sys: ContentEntitySys) => void>(onSysChanged)
  onSysChangeRef.current = onSysChanged

  // when the component is loaded, we have to resize the iframe
  useEffect(() => {
    sdk.window.startAutoResizer({ absoluteElements: true })

    return () => {
      sdk.window.stopAutoResizer()
    }
  }, [document.body.scrollHeight, fieldValue])

  // when SDK is initialized we store the entry version
  useEffect(() => {
    setVersion(sdk.entry.getSys().version)

    // we listen for events from sdk and if the event was a new entry version, we store new version
    const detach = sdk.entry.onSysChanged(sys => {
      // The call must be done inside an anonymous arrow function.
      // Otherwise, onSysChanged will not be executed via the reference but as a memoized function.
      onSysChangeRef.current(sys)
    })

    return () => {
      detach()
    }
  }, [sdk])

  // finally, when the version was updated, we need to fetch the new data
  useEffect(() => {
    if (version) {
      const value = sdk.field.getValue()
      if (value) {
        setFieldValue(value)
      } else {
        setFieldValue(null)
      }
    }
  }, [version])

  /* Internal routing example:
  const entryData = useEntryData()

  const Component = useMemo(() => {
    return ENTRY_FIELDS[entryData.entryContentType ?? "default"] ?? ENTRY_FIELDS["default"]
  }, [entryData.entryContentType]) */

  const Component = useMemo(() => ENTRY_FIELDS['default'], [])

  if (fieldValue === undefined) {
    return <Preloader />
  }

  return <Component data={fieldValue} />
}

export default Field
